import React, { useEffect, useState } from 'react';
import { Box } from 'modules/af_cm_ui_kit';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { StoreReadonlyField } from 'modules/cm/fields/EditableField';
import { CaseDetails } from '../../CaseDetailsPage.types';
import { CaseId } from 'modules/cm/api/cases/types';
import useProductType from 'modules/cm/hooks/useProductType';
import { FieldWithError } from '../../hooks/useFieldError/FieldWithError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { useFormContext } from 'react-hook-form';
import { Resource } from 'modules/cm/utils/resources';
import { useSelector } from 'react-redux';
import { State } from 'modules/cm/store/cases/types';
import { Product } from 'modules/cm/store/cases/products/types';
import { ProductRepaymentTypes } from 'domain/Product';
import { Dict } from 'shared/interface/globalInterface';

interface Props extends FieldWithError {
  caseId: CaseId;
  resources: CaseDetails['resources'];
  onProductChange: () => void;
}

const ProductSection: React.FC<Props> = ({ getErrors, resources, onProductChange }) => {
  const { watch, setValue } = useFormContext();
  const { productTypeChecker } = useProductType();
  const watchProductCode = watch(FIELDS.PRODUCT_CODE);
  const watchProductTier = watch(FIELDS.PRODUCT_TIER);
  const watchProductRepaymentType = watch(FIELDS.PRODUCT_REPAYMENT_TYPE);
  const [isRIO, setIsRIO] = useState<boolean>();
  const [visibleProducts, setVisibleProducts] = useState<Resource[]>([]);
  const [visibleRepymentTypes, setVisibleRepaymentTypes] = useState<any>([]);
  const productsState = useSelector(({ currentCase }: { currentCase: State }) => currentCase.products);

  const repaymentTypeOptionTranslations: Dict = {
    INTEREST_ONLY: 'Interest only',
    CAPITAL_AND_INTEREST: 'Capital and Interest',
  };

  const mapProductsToResources = (products: Product[]) =>
    products.map((product: Product) => ({
      option: product.fullname,
      value: product.code,
    }));

  const mapRepaymentTypesToResources = (repaymentTypes: ProductRepaymentTypes[]) =>
    repaymentTypes.map((repaymentType: ProductRepaymentTypes) => ({
      option: repaymentTypeOptionTranslations[repaymentType],
      value: repaymentType,
    }));

  useEffect(() => {
    if (watchProductCode !== undefined) {
      const isProductType = productTypeChecker(watchProductCode);
      setIsRIO(isProductType('RIO'));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchProductCode]);

  useEffect(() => {
    if (isRIO) {
      setValue(FIELDS.TERM_LENGTH, null);
      setValue(FIELDS.PRODUCT_REPAYMENT_TYPE, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRIO]);

  useEffect(() => {
    setValue(FIELDS.PRODUCT_REPAYMENT_TYPE, watchProductRepaymentType);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchProductRepaymentType]);

  const isProductMatchingTier = (productCode: string) => {
    const pickedProduct = productsState.find((product) => product.code === productCode);

    return pickedProduct ? pickedProduct.tier === watchProductTier : null;
  };

  const getAvailableRepaymentTypesForProduct = (productCode: string) => {
    const pickedProduct = productsState.find((product) => product.code === productCode);

    if (pickedProduct) {
      return pickedProduct.availableRepaymentTypes;
    }

    return [];
  };

  useEffect(() => {
    if (!isProductMatchingTier(watchProductCode)) {
      setValue(FIELDS.PRODUCT_CODE, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchProductTier]);

  useEffect(() => {
    const productFilteredByTier = productsState.filter((product: Product) => product.tier === watchProductTier);
    setVisibleProducts(mapProductsToResources(productFilteredByTier));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchProductTier]);

  useEffect(() => {
    const repaymentTypes = getAvailableRepaymentTypesForProduct(watchProductCode);
    setVisibleRepaymentTypes(mapRepaymentTypesToResources(repaymentTypes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchProductCode]);

  return (
    <Box title="Product">
      <UpdatableField
        fieldType={INPUTS.SELECT}
        field={fields[FIELDS.PRODUCT_TIER]}
        options={resources.tiers}
        getErrors={getErrors}
        withDefaultOption={true}
        required
      />

      {watchProductTier && (
        <UpdatableField
          fieldType={INPUTS.SELECT}
          field={fields[FIELDS.PRODUCT_CODE]}
          searchable={true}
          options={visibleProducts}
          getErrors={getErrors}
          withDefaultOption={true}
          isNested={true}
          required
        />
      )}

      {!isRIO && (
        <UpdatableField
          fieldType={INPUTS.SELECT}
          field={fields[FIELDS.PRODUCT_REPAYMENT_TYPE]}
          options={visibleRepymentTypes}
          getErrors={getErrors}
          withDefaultOption={true}
          isNested={true}
          required
        />
      )}

      <StoreReadonlyField field={fields[FIELDS.PRODUCT_FIXED_TERM]} variant={VARIANTS_TYPES.NUMBER} />

      <UpdatableField
        fieldType={INPUTS.SELECT}
        field={fields[FIELDS.TERM_LENGTH]}
        options={resources.productTermLengths}
        getErrors={getErrors}
        disabled={isRIO}
        searchable={true}
        required
      />

      <StoreReadonlyField field={fields[FIELDS.PRODUCT_FEE]} variant={VARIANTS_TYPES.CURRENCY} />

      <UpdatableField
        fieldType={INPUTS.SELECT}
        field={fields[FIELDS.PRODUCT_FEE_PAYMENT_METHOD]}
        options={resources.productFeePaymentMethods}
        isUpdateable={true}
        getErrors={getErrors}
        required
      />

      <StoreReadonlyField
        field={fields[FIELDS.PRODUCT_FIXED_INTEREST_RATE]}
        variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE}
      />

      <StoreReadonlyField field={fields[FIELDS.PRODUCT_REVERSION_RATE]} variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE} />
    </Box>
  );
};

export default ProductSection;
